"use client";
import React, { useEffect, useMemo, useState } from "react";
import { CardMediaImage } from "modules/common/components/CardMediaImage";
import { flippableCardLayouts } from "modules/common/model/models";
import {
  AutocompleteGameCard,
  CommonPagedCard,
  GameCard,
} from "modules/cards/model/card";
import { hasFoilVisual } from "modules/cards/model/finishBehavior";
import { cn } from "utils/utils";
import { PortalTooltip } from "modules/common/components/PortalTooltip";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { CollectionCard } from "modules/collections/details/model/collection";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { DeckCard } from "modules/decks/model/deck";
import { ProposalItem } from "modules/trade-proposals/model/tradeProposal";

type TooltipPlacement = "top" | "right" | "bottom" | "left";

const placementStyles: Record<TooltipPlacement, string> = {
  top: "mb-2",
  bottom: "mt-2",
  left: "mr-2",
  right: "ml-2",
};

interface Props {
  card:
    | GameCard
    | AutocompleteGameCard
    | CollectionCard
    | TradeItem
    | DeckCard
    | CommonPagedCard
    | ProposalItem;
  children: React.ReactNode;
  isFoil?: boolean;
  placement?: TooltipPlacement;
  forceOpen?: boolean;
  className?: string;
  /**
   * Opt-in: on mobile (no hover), tapping the trigger toggles the card-image
   * tooltip open instead of doing nothing. Desktop keeps the hover behavior.
   */
  tapToShowOnMobile?: boolean;
}

export function TailwindTooltipCardImage({
  card,
  children,
  isFoil,
  placement = "right",
  forceOpen = false,
  className,
  tapToShowOnMobile = false,
}: Props) {
  const isCardFoil = isFoil ?? hasFoilVisual(card.finishes ?? []);
  const hasFlippableFace =
    "features" in card &&
    "layout" in card.features &&
    "otherFaces" in card.features &&
    !!card.features.layout &&
    flippableCardLayouts.includes(card.features.layout) &&
    card.features.otherFaces;

  // No hover on touch: below `lg` (the breakpoint the tooltip content shows at),
  // let a tap toggle the tooltip. useMediaQuery is SSR-safe (false until mount).
  const isMobile = useMediaQuery("(max-width: 1023px)");
  const tapMode = tapToShowOnMobile && isMobile;
  const [tappedOpen, setTappedOpen] = useState(false);

  // While open in tap mode, the next tap anywhere (or Escape) closes it.
  // Deferred so the opening tap itself doesn't immediately close it.
  useEffect(() => {
    if (!tapMode || !tappedOpen) return;
    const close = () => setTappedOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTappedOpen(false);
    };
    const id = window.setTimeout(() => {
      document.addEventListener("click", close);
      document.addEventListener("keydown", onKey);
    }, 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [tapMode, tappedOpen]);

  const tooltipContent = useMemo(
    () => (
      <div
        className={cn(
          "shadow-lg",
          tapMode ? "flex" : "hidden lg:flex",
          placementStyles[placement]
        )}
      >
        <CardMediaImage
          isFoil={isCardFoil}
          card={card}
          flipped={false}
          setFlipped={() => {}}
          isTooltip
          loading={"lazy"}
          priority={false}
          fetchPriority={"low"}
        />
        {hasFlippableFace && (
          <CardMediaImage
            isFoil={isCardFoil}
            card={card}
            flipped
            setFlipped={() => {}}
            isTooltip
            loading={"lazy"}
            priority={false}
            fetchPriority={"low"}
          />
        )}
      </div>
    ),
    [card, hasFlippableFace, isCardFoil, placement, tapMode]
  );

  // In tap mode, capture the tap to toggle the preview without triggering the
  // surrounding control (e.g. the picker row's select handler). `contents`
  // keeps the original layout — the wrapper renders no box of its own.
  const trigger = tapMode ? (
    <span
      className="contents"
      onClick={(e) => {
        e.stopPropagation();
        setTappedOpen((prev) => !prev);
      }}
    >
      {children}
    </span>
  ) : (
    children
  );

  return (
    <PortalTooltip
      content={tooltipContent}
      placement={placement}
      forceOpen={tapMode ? tappedOpen : forceOpen}
      className={className}
    >
      {trigger}
    </PortalTooltip>
  );
}
